import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { formatDisplayDate, formatLongDate } from "@/lib/dateFormat";
import { useAuth } from "@/context/AuthContext";
import AdminLayout from "@/components/AdminLayout";
import {
  Plus,
  Search,
  Trash2,
  Check,
  Eye,
  Banknote,
} from "lucide-react";
import { SortableTH, useSortableData, EmptyState } from "@/components/SortableTable";
import TableSkeleton from "@/components/TableSkeleton";
import RowActionsMenu from "@/components/RowActionsMenu";
import Banner from "@/components/Banner";

const REASONS = ["Appraisal", "Promotion", "Correction", "Market Adjustment", "Other"];
const STATUSES = ["All", "draft", "approved"];

const STATUS_COLORS = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  approved: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

const ELIGIBLE = new Set(["Active", "Onboarding", "Probation", "Notice Period"]);

const inputCls =
  "w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#226DB4]/30 focus:border-[#226DB4] bg-white";

const fmtInr = (n) =>
  n == null || n === ""
    ? "—"
    : Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const EMPTY_FORM = {
  employee_id: "",
  effective_from: "",
  reason: "Appraisal",
  notes: "",
  previous_annual_ctc: "",
  annual_ctc: "",
  basic: "",
  hra: "",
  conveyance: "",
  medical: "",
  special: "",
  tds_rate: "",
};

export default function SalaryRevisions() {
  const { user } = useAuth();
  const isStrictAdmin = user?.role === "admin";
  const hasAction = (module, action) => {
    if (isStrictAdmin) return true;
    const eff = user?._effective_perms;
    if (eff && eff[module]) return Boolean(eff[module][action]);
    return false;
  };
  const canSee = hasAction("salary_revisions", "view") || hasAction("salary_revisions", "add");
  const canWrite = hasAction("salary_revisions", "add") || hasAction("salary_revisions", "edit");
  const canApprove = hasAction("salary_revisions", "approve");
  const canDelete = hasAction("salary_revisions", "delete");
  const canAct = canWrite || canApprove || canDelete;

  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [banner, setBanner] = useState(null);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const [impact, setImpact] = useState(null);
  const [impactLoading, setImpactLoading] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const employeeIdParam = searchParams.get("employee_id") || "";

  const loadAll = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "All") params.status = statusFilter;
      if (employeeIdParam) params.employee_id = employeeIdParam;
      const [r, e] = await Promise.all([
        api.get("/salary-revisions", { params }),
        api.get("/employees").catch(() => ({ data: [] })),
      ]);
      setItems(r.data || []);
      setEmployees(
        (e.data || []).filter(
          (emp) =>
            ELIGIBLE.has(emp.employment?.status) &&
            emp.employment?.employment_type !== "Intern" &&
            !emp.employment?.is_archived_intern,
        ),
      );
    } catch (ex) {
      setBanner({ type: "error", message: ex?.response?.data?.detail || "Failed to load revisions" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, employeeIdParam]);

  const filtered = useMemo(() => {
    let list = items;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (r) =>
          (r.employee_name || "").toLowerCase().includes(q) ||
          (r.employee_code || "").toLowerCase().includes(q) ||
          (r.reason || "").toLowerCase().includes(q),
      );
    }
    return list;
  }, [items, search]);

  const { items: sorted, sortBy, sortDir, requestSort } = useSortableData(filtered, {
    initialKey: "effective_from",
    accessors: {
      effective_from: (r) => r.effective_from || "",
      old_ctc: (r) => Number(r.old_annual_ctc || 0),
      new_ctc: (r) => Number(r.new_annual_ctc || 0),
      created_at: (r) => r.created_at || "",
    },
  });

  const openCreate = () => {
    const prefEmp = employeeIdParam
      ? employees.find((e) => e.id === employeeIdParam)
      : null;
    const sal = prefEmp?.salary || {};
    setForm({
      ...EMPTY_FORM,
      employee_id: prefEmp?.id || "",
      previous_annual_ctc: sal.annual_ctc || "",
      annual_ctc: "",
      basic: "",
      hra: "",
      conveyance: "",
      medical: "",
      special: "",
      tds_rate: "",
    });
    setErr("");
    setOpen(true);
  };

  const onEmployeeChange = (id) => {
    const emp = employees.find((e) => e.id === id);
    const sal = emp?.salary || {};
    setForm((f) => ({
      ...f,
      employee_id: id,
      previous_annual_ctc: sal.annual_ctc || "",
      annual_ctc: "",
      basic: "",
      hra: "",
      conveyance: "",
      medical: "",
      special: "",
      tds_rate: "",
    }));
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!form.employee_id || !form.effective_from || !form.annual_ctc) {
      setErr("Employee, effective date, and Revised Annual CTC are required");
      return;
    }
    setSaving(true);
    setErr("");
    try {
      const new_salary = { annual_ctc: Number(form.annual_ctc) };
      ["basic", "hra", "conveyance", "medical", "special", "tds_rate"].forEach((k) => {
        if (form[k] !== "" && form[k] != null) new_salary[k] = Number(form[k]);
      });
      const payload = {
        employee_id: form.employee_id,
        effective_from: form.effective_from,
        reason: form.reason,
        notes: form.notes || undefined,
        new_salary,
      };
      if (form.previous_annual_ctc !== "" && form.previous_annual_ctc != null) {
        payload.old_annual_ctc = Number(form.previous_annual_ctc);
      }
      await api.post("/salary-revisions", payload);
      setOpen(false);
      setBanner({ type: "success", message: "Draft salary revision created" });
      await loadAll();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Failed to create revision");
    } finally {
      setSaving(false);
    }
  };

  const runAction = async (id, action) => {
    try {
      const { data } = await api.post(`/salary-revisions/${id}/${action}`);
      if (action === "approve") {
        const total = data?.arrears_suggestion?.total_suggested;
        const rev = data?.revision || data;
        setBanner({
          type: "success",
          message:
            total > 0
              ? `Approved — salary updated. Suggested arrears ₹${fmtInr(total)} — add manually on next open payslip.`
              : "Approved — employee salary updated. Next payroll will use the new CTC.",
        });
        if (data?.arrears_suggestion) {
          setImpact({ revision_id: id, ...data.arrears_suggestion, from_apply: true });
        }
      } else {
        setBanner({ type: "success", message: `Revision ${action}d` });
      }
      await loadAll();
    } catch (ex) {
      setBanner({ type: "error", message: ex?.response?.data?.detail || `Failed to ${action}` });
    }
  };

  const deleteDraft = async (id) => {
    if (!window.confirm("Delete this draft revision?")) return;
    try {
      await api.delete(`/salary-revisions/${id}`);
      setBanner({ type: "success", message: "Draft deleted" });
      await loadAll();
    } catch (ex) {
      setBanner({ type: "error", message: ex?.response?.data?.detail || "Delete failed" });
    }
  };

  const viewImpact = async (id) => {
    setImpactLoading(true);
    try {
      const { data } = await api.get(`/salary-revisions/${id}/impact`);
      setImpact({ revision_id: id, ...data });
    } catch (ex) {
      setBanner({ type: "error", message: ex?.response?.data?.detail || "Failed to load impact" });
    } finally {
      setImpactLoading(false);
    }
  };

  const clearEmployeeFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("employee_id");
    setSearchParams(next, { replace: true });
  };

  return (
    <AdminLayout title="Salary Revisions" breadcrumbs={["Home", "Performance", "Salary Revisions"]}>
      <div className="space-y-4" data-testid="salary-revisions-page">
        {banner && (
          <Banner variant={banner.type || "info"} onClose={() => setBanner(null)}>
            {banner.message}
          </Banner>
        )}

        <div className="rounded border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
          <div className="font-semibold flex items-center gap-2">
            <Banknote className="w-4 h-4" /> How statuses work
          </div>
          <p className="mt-1 text-sky-800/90">
            <b>Draft</b> — HR creates the revision (not final).{" "}
            <b>Approved</b> — confirms and updates the employee salary master; next payroll uses it
            (mid-month effective dates auto-blend old + new). Prior paid months are not rewritten —
            use the arrears suggestion after Approve.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
              <input
                className={`${inputCls} pl-8 w-56`}
                placeholder="Search employee / reason"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <select
              className={`${inputCls} w-36`}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s === "All" ? "All statuses" : s}
                </option>
              ))}
            </select>
            {employeeIdParam && (
              <button
                type="button"
                onClick={clearEmployeeFilter}
                className="text-xs text-[#226DB4] hover:underline"
              >
                Clear employee filter
              </button>
            )}
          </div>
          {canWrite && (
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex items-center gap-1.5 bg-[#226DB4] text-white text-sm px-3 py-2 rounded hover:bg-[#1B5A94]"
            >
              <Plus className="w-4 h-4" /> New revision
            </button>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded overflow-x-auto">
          {loading ? (
            <TableSkeleton rows={6} cols={7} />
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                <tr>
                  <SortableTH columnKey="employee_name" active={sortBy} dir={sortDir} onClick={requestSort}>
                    Employee
                  </SortableTH>
                  <SortableTH columnKey="new_ctc" active={sortBy} dir={sortDir} onClick={requestSort}>
                    Old → New CTC
                  </SortableTH>
                  <SortableTH columnKey="effective_from" active={sortBy} dir={sortDir} onClick={requestSort}>
                    Effective from
                  </SortableTH>
                  <th className="px-3 py-2 text-left">Reason</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <SortableTH columnKey="created_at" active={sortBy} dir={sortDir} onClick={requestSort}>
                    Created
                  </SortableTH>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sorted.length === 0 ? (
                  <EmptyState message="No salary revisions yet" colSpan={7} />
                ) : (
                  sorted.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50/60">
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-800">{r.employee_name}</div>
                      <div className="text-xs text-slate-500">{r.employee_code}</div>
                    </td>
                    <td className="px-3 py-2">
                      ₹{fmtInr(r.old_annual_ctc)} → <span className="font-semibold">₹{fmtInr(r.new_annual_ctc)}</span>
                    </td>
                    <td className="px-3 py-2">{formatDisplayDate(r.effective_from)}</td>
                    <td className="px-3 py-2">{r.reason}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex text-xs border px-2 py-0.5 rounded ${STATUS_COLORS[r.status] || ""}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      {formatLongDate(r.created_at)}
                      {r.approved_at && (
                        <div>Approved {formatLongDate(r.approved_at)}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <RowActionsMenu
                        items={[
                          {
                            label: "View impact",
                            icon: Eye,
                            onClick: () => viewImpact(r.id),
                          },
                          (r.status === "draft" || (r.status === "approved" && !r.finalized && !r.applied_at)) &&
                            canApprove && {
                              label: "Approve",
                              icon: Check,
                              onClick: () => {
                                if (
                                  !window.confirm(
                                    "Approve this revision? Employee salary will be updated now.",
                                  )
                                )
                                  return;
                                runAction(r.id, "approve");
                              },
                            },
                          r.status === "draft" &&
                            canDelete && {
                              label: "Delete draft",
                              icon: Trash2,
                              danger: true,
                              onClick: () => deleteDraft(r.id),
                            },
                        ].filter(Boolean)}
                      />
                    </td>
                  </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <form
            onSubmit={submitCreate}
            className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto"
          >
            <h3 className="text-lg font-semibold text-slate-800">New salary revision</h3>
            {err && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded px-3 py-2">{err}</div>}
            <div>
              <label className="text-xs font-medium text-slate-600">Employee</label>
              <select
                className={inputCls}
                value={form.employee_id}
                onChange={(e) => onEmployeeChange(e.target.value)}
                required
              >
                <option value="">Select employee</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.basic?.code} — {emp.basic?.first_name} {emp.basic?.last_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Effective from</label>
                <input
                  type="date"
                  className={inputCls}
                  value={form.effective_from}
                  onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Reason</label>
                <select
                  className={inputCls}
                  value={form.reason}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                >
                  {REASONS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Previous Annual CTC</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  className={inputCls}
                  value={form.previous_annual_ctc}
                  onChange={(e) => setForm({ ...form, previous_annual_ctc: e.target.value })}
                  placeholder="From employee master (editable)"
                  data-testid="sal-rev-previous-ctc"
                />
                <p className="text-[10px] text-slate-500 mt-1">
                  Used on Compensation Revision Letter as Previous Annual CTC. Enter manually if master was blank.
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Revised Annual CTC *</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  className={inputCls}
                  value={form.annual_ctc}
                  onChange={(e) => setForm({ ...form, annual_ctc: e.target.value })}
                  required
                  data-testid="sal-rev-new-ctc"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {["basic", "hra", "conveyance", "medical", "special", "tds_rate"].map((k) => (
                <div key={k}>
                  <label className="text-xs font-medium text-slate-600 capitalize">
                    {k === "tds_rate" ? "TDS rate %" : k} (optional)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className={inputCls}
                    value={form[k]}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  />
                </div>
              ))}
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Notes</label>
              <textarea
                className={inputCls}
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="px-3 py-2 text-sm border rounded" onClick={() => setOpen(false)}>
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-3 py-2 text-sm bg-[#226DB4] text-white rounded disabled:opacity-50"
              >
                {saving ? "Saving…" : "Create draft"}
              </button>
            </div>
          </form>
        </div>
      )}

      {(impact || impactLoading) && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 space-y-3 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-slate-800">
              {impact?.from_apply ? "Arrears suggestion" : "Revision impact"}
            </h3>
            {impactLoading && <div className="text-sm text-slate-500">Loading…</div>}
            {impact && !impactLoading && (
              <>
                {impact.effective_month?.segments && (
                  <div>
                    <div className="text-xs font-semibold uppercase text-slate-500 mb-1">
                      Effective month split ({formatDisplayDate(impact.effective_from)})
                    </div>
                    <ul className="text-sm space-y-1">
                      {impact.effective_month.segments.map((s, i) => (
                        <li key={i} className="border border-slate-100 rounded px-2 py-1.5">
                          {formatDisplayDate(s.start)} → {formatDisplayDate(s.end)}: <strong>{s.days}</strong> working days · CTC ₹
                          {fmtInr(s.annual_ctc)} · monthly ₹{fmtInr(s.monthly_gross)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {(impact.arrears_suggestion || impact.months) && (
                  <div>
                    <div className="text-xs font-semibold uppercase text-slate-500 mb-1">Prior months</div>
                    {(() => {
                      const a = impact.arrears_suggestion || impact;
                      return (
                        <>
                          <p className="text-sm text-slate-600 mb-2">
                            Suggested total: <strong>₹{fmtInr(a.total_suggested)}</strong>
                            {" "}(Δ monthly ₹{fmtInr(a.full_month_delta)})
                          </p>
                          <ul className="text-xs space-y-1 max-h-40 overflow-y-auto">
                            {(a.months || []).map((m) => (
                              <li key={`${m.year}-${m.month}`} className="flex justify-between gap-2">
                                <span>
                                  {m.period_label}
                                  {!m.payslip_exists && " (no payslip)"}
                                </span>
                                <span>₹{fmtInr(m.delta)}</span>
                              </li>
                            ))}
                          </ul>
                          <p className="text-xs text-slate-500 mt-2">{a.note}</p>
                        </>
                      );
                    })()}
                  </div>
                )}
              </>
            )}
            <div className="flex justify-end pt-2">
              <button type="button" className="px-3 py-2 text-sm border rounded" onClick={() => setImpact(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
